import { useCallback, useEffect, useState } from 'react';
import { peopleGateway } from '../../index';
import {
  federalFilingStatusLabels,
  filingStatusesByW4Version,
  w4FormVersionLabels,
  type FederalTaxElectionResponse,
  type SaveFederalTaxElectionInput,
} from '../../types';

/**
 * Federal Income Tax (EPN-193). Stores the employee's federal W-4 answers only; nothing here calculates tax. ADR 0004 is a
 * PROPOSAL and this is a staging / synthetic-data slice. Layout and labels follow the approved "Federal Income Tax" frames
 * (2020 or later / 2019 or earlier); the Exempt choice and the effective date are not in those frames (UX / DESIGN GAP).
 * The entered values are never written to logs or the console.
 */
export function FederalTaxElectionSection({ employeeId }: { employeeId: string }) {
  const [data, setData] = useState<FederalTaxElectionResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [version, setVersion] = useState(1);
  const [status, setStatus] = useState('');
  const [allowances, setAllowances] = useState('');
  const [extra, setExtra] = useState('');
  const [multipleJobs, setMultipleJobs] = useState<boolean | null>(null);
  const [dependents, setDependents] = useState('');
  const [otherIncome, setOtherIncome] = useState('');
  const [deductions, setDeductions] = useState('');
  const [exempt, setExempt] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const apply = useCallback((r: FederalTaxElectionResponse) => {
    setData(r);
    const c = r.current;
    // A pre-2020 election stays as recorded; changing it uses the current form, so the form starts blank on the 2020 version.
    const useCurrent = c !== null && c.formVersion === 1;
    setVersion(1);
    setStatus(useCurrent ? String(c.filingStatus) : '');
    setAllowances('');
    setExtra(useCurrent && c.extraWithholding ? String(c.extraWithholding) : '');
    setMultipleJobs(useCurrent ? c.multipleJobs : null);
    setDependents(useCurrent && c.dependentsAmount ? String(c.dependentsAmount) : '');
    setOtherIncome(useCurrent && c.otherIncome ? String(c.otherIncome) : '');
    setDeductions(useCurrent && c.deductions ? String(c.deductions) : '');
    setExempt(useCurrent ? c.isExempt : false);
    setEffectiveDate('');
  }, []);

  const fetchElection = useCallback(
    () =>
      peopleGateway
        .getFederalTaxElection(employeeId)
        .then(apply)
        .catch(() => setLoadError('Could not load the federal tax election.'))
        .finally(() => setLoading(false)),
    [employeeId, apply],
  );

  useEffect(() => {
    void fetchElection();
  }, [fetchElection]);

  function retry() {
    setLoading(true);
    setLoadError(null);
    void fetchElection();
  }

  const hasElection = data?.current != null;
  const pre = version === 0;

  function changeVersion(v: number) {
    setVersion(v);
    setStatus('');
    setSaved(false);
  }

  function money(text: string, label: string): number | null | undefined {
    const t = text.trim();
    if (t === '') return null;
    if (!/^\d+(\.\d{1,2})?$/.test(t)) {
      setError(`${label} must be zero or more, with at most two decimal places.`);
      return undefined;
    }
    return Number(t);
  }

  async function save() {
    setError(null);
    setSaved(false);
    if (status === '') {
      setError('Please choose a filing status.');
      return;
    }
    if (hasElection && effectiveDate === '') {
      setError('Please enter the date this change takes effect.');
      return;
    }
    const input: SaveFederalTaxElectionInput = {
      formVersion: version, filingStatus: Number(status), isExempt: exempt, effectiveDate: effectiveDate || null,
    };
    const extraValue = money(extra, pre ? 'Extra withholding per pay period' : 'Most recent withholding amount');
    if (extraValue === undefined) return;
    input.extraWithholding = extraValue;
    if (pre) {
      if (!exempt) {
        if (!/^\d+$/.test(allowances.trim())) {
          setError('Total allowances must be a whole number, zero or more.');
          return;
        }
        input.allowances = Number(allowances.trim());
      }
    } else {
      const dep = money(dependents, 'Dependent amount');
      const oth = money(otherIncome, 'Other income');
      const ded = money(deductions, 'Deductions');
      if (dep === undefined || oth === undefined || ded === undefined) return;
      input.dependentsAmount = dep;
      input.otherIncome = oth;
      input.deductions = ded;
      input.multipleJobs = multipleJobs ?? false;
    }
    setSaving(true);
    try {
      apply(await peopleGateway.saveFederalTaxElection(employeeId, input));
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the federal tax election.');
    } finally {
      setSaving(false);
    }
  }

  const title = <h3 className="profile-section-title">Federal Income Tax</h3>;

  if (loading) {
    return (
      <section className="profile-section" aria-busy="true">
        {title}
        <p className="muted" role="status">Loading the federal tax election…</p>
      </section>
    );
  }
  if (loadError) {
    return (
      <section className="profile-section">
        {title}
        <p className="error" role="alert">{loadError}</p>
        <button type="button" onClick={retry}>Retry</button>
      </section>
    );
  }

  const current = data?.current ?? null;
  return (
    <section className="profile-section">
      {title}
      <p className="muted">
        Staging with synthetic data only. This records the employee&apos;s federal W-4 answers; it does not calculate or withhold any tax.
        New and changed elections use the 2020 or later W-4. An election recorded on a 2019 or earlier W-4 is kept exactly as recorded.
      </p>
      {current && (
        <p className="muted" style={{ marginTop: 8 }}>
          Current election: {w4FormVersionLabels[current.formVersion]}, {federalFilingStatusLabels[current.filingStatus]}
          {current.isExempt ? ', exempt' : ''}, effective {current.effectiveFrom}.
          {current.formVersion === 0 ? ' Recorded on the 2019 or earlier form; saving a change below records a new election on the 2020 or later form.' : ''}
        </p>
      )}
      <div className="profile-field-grid">
        <label>
          W4 Form Version *
          <select value={version} onChange={(e) => changeVersion(Number(e.target.value))} disabled={hasElection}>
            <option value={1}>{w4FormVersionLabels[1]}</option>
            <option value={0}>{w4FormVersionLabels[0]}</option>
          </select>
        </label>
        <label>
          Filing Status *
          <select value={status} onChange={(e) => { setStatus(e.target.value); setSaved(false); }}>
            <option value="">Select a filing status</option>
            {filingStatusesByW4Version[version]!.map((s) => (
              <option key={s} value={s}>{federalFilingStatusLabels[s]}</option>
            ))}
          </select>
        </label>
        {pre ? (
          <>
            <label>
              Total Allowances *
              <input inputMode="numeric" value={allowances} onChange={(e) => setAllowances(e.target.value)} placeholder="0" disabled={exempt} />
            </label>
            <label>
              Extra Withholding per Pay Period ($)
              <input inputMode="decimal" value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="00.00" disabled={exempt} />
            </label>
          </>
        ) : (
          <>
            <label>
              Dependent Amount ($)
              <input inputMode="decimal" value={dependents} onChange={(e) => setDependents(e.target.value)} placeholder="00.00" disabled={exempt} />
            </label>
            <label>
              Other Income ($)
              <input inputMode="decimal" value={otherIncome} onChange={(e) => setOtherIncome(e.target.value)} placeholder="00.00" disabled={exempt} />
            </label>
            <label>
              Deductions ($)
              <input inputMode="decimal" value={deductions} onChange={(e) => setDeductions(e.target.value)} placeholder="00.00" disabled={exempt} />
            </label>
            <label>
              Most Recent Withholding Amount ($)
              <input inputMode="decimal" value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="00.00" disabled={exempt} />
            </label>
            <fieldset disabled={exempt} style={{ border: 0, padding: 0, margin: 0 }}>
              <legend>Does employee have two jobs?</legend>
              <label style={{ display: 'inline-flex', gap: 6, marginRight: 16 }}>
                <input type="radio" name="two-jobs" checked={multipleJobs === true} onChange={() => setMultipleJobs(true)} /> YES
              </label>
              <label style={{ display: 'inline-flex', gap: 6 }}>
                <input type="radio" name="two-jobs" checked={multipleJobs === false} onChange={() => setMultipleJobs(false)} /> NO
              </label>
            </fieldset>
          </>
        )}
        <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={exempt}
            onChange={(e) => {
              setExempt(e.target.checked);
              if (e.target.checked) { setAllowances(''); setExtra(''); setDependents(''); setOtherIncome(''); setDeductions(''); setMultipleJobs(null); }
            }}
          />
          Exempt from federal income tax withholding
        </label>
        {hasElection && (
          <label>
            Effective Date *
            <input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} />
          </label>
        )}
      </div>
      {error && <p className="error" role="alert">{error}</p>}
      {saved && <p className="muted" role="status">Federal tax election saved.</p>}
      <div style={{ marginTop: 12 }}>
        <button type="button" onClick={() => void save()} disabled={saving}>
          {saving ? 'Saving…' : 'Save Federal Tax Election'}
        </button>
      </div>
      {data && data.history.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <h4>Election history</h4>
          <ul aria-label="Federal tax election history">
            {data.history.map((h) => (
              <li key={`${h.effectiveFrom}-${h.formVersion}`}>
                Effective {h.effectiveFrom}: {w4FormVersionLabels[h.formVersion]}{h.isCurrent ? ' (current)' : ''}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
